import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo, JsonLd } from '@/components/Seo';
import { PublicNav, PublicFooter, AuthChooser } from '@/components/PublicChrome';
import { HomeAnnouncement } from '@/components/HomeAnnouncement';
import {
  ArrowRight, Check, ChevronDown,
  ShieldCheck, IndianRupee, Globe, HeadphonesIcon,
  FileText, MessageSquare, Truck,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Sections                                                           */
/* ------------------------------------------------------------------ */

function Hero() {
  const { t } = useTranslation('homepage');
  return (
    <section className="pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-amber-500/10 text-amber-600 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            {t('hero.eyebrow')}
          </span>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] text-graphite-900">
            {t('hero.titleA')}<br/>{t('hero.titleB')} <span className="text-amber-500">{t('hero.titleAccent')}</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg text-graphite-500 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-[520px]" data-testid="hero-actions">
            <Link to="/buyer/login" data-testid="hero-cta-garage" className="buyer-btn h-12 px-4 text-sm w-full">
              {t('hero.loginGarage')} <ArrowRight size={16} className="hidden sm:block" />
            </Link>
            <Link to="/seller/login" data-testid="hero-login-supplier" className="supplier-btn h-12 px-4 text-sm w-full">
              {t('hero.loginSupplier')} <ArrowRight size={16} className="hidden sm:block" />
            </Link>
          </div>

          <div className="mt-3 text-sm text-graphite-500" data-testid="hero-login-prompt">
            {t('hero.loginPrompt')}
          </div>

          <div className="mt-10 flex items-center gap-6 text-sm text-graphite-500 flex-wrap">
            <div className="flex items-center gap-2"><Check size={16} className="text-amber-500" /> {t('hero.trust1')}</div>
            <div className="flex items-center gap-2"><Check size={16} className="text-amber-500" /> {t('hero.trust2')}</div>
            <div className="hidden sm:flex items-center gap-2"><Check size={16} className="text-amber-500" /> {t('hero.trust3')}</div>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -inset-6 bg-paper-200 rounded-3xl -z-10" aria-hidden="true" />
            <picture>
              <source
                type="image/webp"
                srcSet="/hero-768.webp 768w, /hero-1200.webp 1200w, /hero.webp 1536w"
                sizes="(min-width: 1024px) 46vw, 100vw"
              />
              <img
                src="/hero-768.png"
                alt="Garages posting enquiries and receiving quotes from verified spare parts suppliers"
                className="w-full h-auto rounded-3xl"
                width="1536"
                height="1024"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                data-testid="hero-image"
              />
            </picture>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation('homepage');
  const steps = [
    { n: 1, icon: FileText,      title: t('howItWorks.step1Title'), body: t('howItWorks.step1Body') },
    { n: 2, icon: MessageSquare, title: t('howItWorks.step2Title'), body: t('howItWorks.step2Body') },
    { n: 3, icon: Truck,         title: t('howItWorks.step3Title'), body: t('howItWorks.step3Body') },
  ];
  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-paper-200">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="overline text-amber-600">{t('howItWorks.eyebrow')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-graphite-900 tracking-tight">
            {t('howItWorks.title')}
          </h2>
          <p className="mt-3 text-graphite-500 text-lg">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-white border border-graphite-border rounded-2xl p-7 hover:-translate-y-1 hover:shadow-soft-lg transition-[transform,box-shadow] duration-200">
              <div className="flex items-center gap-3">
                <span className="inline-flex w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 items-center justify-center font-heading font-bold text-lg">
                  {s.n}
                </span>
                <s.icon size={20} className="text-graphite-400" />
              </div>
              <h3 className="mt-6 font-heading font-bold text-xl text-graphite-900">{s.title}</h3>
              <p className="mt-2 text-graphite-500 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChecklistItem({ text, tint }) {
  return (
    <li className="flex items-start gap-3">
      <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full grid place-items-center ${tint}`}>
        <Check size={12} className="text-white" strokeWidth={3} />
      </span>
      <span className="text-graphite-700">{text}</span>
    </li>
  );
}

function WhyChoose() {
  const { t } = useTranslation('homepage');
  const garageList = t('whyChoose.garageList', { returnObjects: true }) || [];
  const supplierList = t('whyChoose.supplierList', { returnObjects: true }) || [];

  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="overline text-amber-600">{t('whyChoose.eyebrow')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-graphite-900 tracking-tight">
            {t('whyChoose.title')}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div id="for-garages" className="bg-white border border-graphite-border rounded-2xl p-8 hover:shadow-soft-lg transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-7 px-3 rounded-full bg-amber-500/10 text-amber-600 items-center text-xs font-semibold">{t('whyChoose.garageTag')}</span>
            </div>
            <h3 className="mt-4 font-heading font-bold text-2xl text-graphite-900">{t('whyChoose.garageTitle')}</h3>
            <ul className="mt-6 space-y-3">
              {garageList.map((txt) => <ChecklistItem key={txt} text={txt} tint="bg-amber-500" />)}
            </ul>
            <Link to="/buyer/login" data-testid="whychoose-cta-garage" className="mt-8 buyer-btn h-11 px-5">
              {t('hero.loginGarage')} <ArrowRight size={16} />
            </Link>
          </div>

          <div id="for-suppliers" className="bg-white border border-graphite-border rounded-2xl p-8 hover:shadow-soft-lg transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-7 px-3 rounded-full bg-supplier-500/10 text-supplier-500 items-center text-xs font-semibold">{t('whyChoose.supplierTag')}</span>
            </div>
            <h3 className="mt-4 font-heading font-bold text-2xl text-graphite-900">{t('whyChoose.supplierTitle')}</h3>
            <ul className="mt-6 space-y-3">
              {supplierList.map((txt) => <ChecklistItem key={txt} text={txt} tint="bg-supplier-500" />)}
            </ul>
            <Link to="/seller/register" data-testid="whychoose-cta-supplier" className="mt-8 supplier-btn h-11 px-5">
              {t('whyChoose.supplierCta')} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustCards() {
  const { t } = useTranslation('homepage');
  const cards = [
    { icon: ShieldCheck,    title: t('trust.verifiedTitle'), body: t('trust.verifiedBody') },
    { icon: IndianRupee,    title: t('trust.pricingTitle'),  body: t('trust.pricingBody') },
    { icon: Globe,          title: t('trust.networkTitle'),  body: t('trust.networkBody') },
    { icon: HeadphonesIcon, title: t('trust.supportTitle'),  body: t('trust.supportBody') },
  ];
  return (
    <section className="py-20 lg:py-28 bg-paper-200">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="overline text-amber-600">{t('trust.eyebrow')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-graphite-900 tracking-tight">
            {t('trust.title')}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c) => (
            <div key={c.title} className="bg-white border border-graphite-border rounded-2xl p-6 hover:-translate-y-1 hover:shadow-soft-lg transition-[transform,box-shadow] duration-200">
              <span className="inline-flex w-11 h-11 rounded-xl bg-amber-500/10 text-amber-600 items-center justify-center">
                <c.icon size={20} />
              </span>
              <h3 className="mt-5 font-heading font-bold text-lg text-graphite-900">{c.title}</h3>
              <p className="mt-2 text-sm text-graphite-500 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSplit() {
  const { t } = useTranslation('homepage');
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-graphite-900 tracking-tight">
            {t('cta.title')}
          </h2>
          <p className="mt-4 text-lg text-graphite-500">
            {t('cta.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-graphite-border rounded-2xl p-8 hover:shadow-soft-lg transition-shadow duration-200">
            <span className="inline-flex h-7 px-3 rounded-full bg-amber-500/10 text-amber-600 items-center text-xs font-semibold">{t('cta.garageTag')}</span>
            <h3 className="mt-4 font-heading font-bold text-2xl text-graphite-900">{t('cta.garageTitle')}</h3>
            <p className="mt-2 text-graphite-500">{t('cta.garageBody')}</p>
            <div className="mt-6">
              <Link to="/buyer/login" data-testid="cta-split-garage" className="buyer-btn h-11 px-5 w-full">
                {t('hero.loginGarage')} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="bg-white border border-graphite-border rounded-2xl p-8 hover:shadow-soft-lg transition-shadow duration-200">
            <span className="inline-flex h-7 px-3 rounded-full bg-supplier-500/10 text-supplier-500 items-center text-xs font-semibold">{t('cta.supplierTag')}</span>
            <h3 className="mt-4 font-heading font-bold text-2xl text-graphite-900">{t('cta.supplierTitle')}</h3>
            <p className="mt-2 text-graphite-500">{t('cta.supplierBody')}</p>
            <div className="mt-6">
              <Link to="/seller/login" data-testid="cta-split-supplier-login" className="supplier-btn h-11 px-5 w-full sm:w-auto">
                {t('hero.loginSupplier')} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useTranslation('homepage');
  const items = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="py-20 lg:py-28 bg-paper-200">
      <div className="max-w-[860px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="overline text-amber-600">{t('faq.eyebrow')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-graphite-900 tracking-tight">
            {t('faq.title')}
          </h2>
        </div>

        <div className="bg-white border border-graphite-border rounded-2xl divide-y divide-graphite-border overflow-hidden">
          {items.map((it, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={it.q}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-paper-200/40 transition-colors duration-150"
                >
                  <span className="font-semibold text-graphite-900">{it.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-graphite-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 -mt-1 text-graphite-500 leading-relaxed">{it.a}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function Landing() {
  const [chooser, setChooser] = useState(null);
  const { t } = useTranslation('homepage');
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [1, 2, 3, 4].map((i) => ({
      '@type': 'Question',
      name: t(`faq.q${i}`),
      acceptedAnswer: { '@type': 'Answer', text: t(`faq.a${i}`) },
    })),
  };
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Car Spare Parts Online for Garages & Workshops | SparesHub"
        description="Post one enquiry and compare quotations from verified car spare parts sellers across India. Genuine and aftermarket parts, GST invoice and doorstep delivery."
        canonicalPath="/"
      />
      <JsonLd id="ld-faq" data={faqLd} />
      <HomeAnnouncement />
      <PublicNav onChoose={setChooser} />
      <main>
        <Hero />
        <HowItWorks />
        <WhyChoose />
        <TrustCards />
        <CtaSplit />
        <Faq />
      </main>
      <PublicFooter />
      <AuthChooser mode={chooser} onClose={() => setChooser(null)} />
    </div>
  );
}
